import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowBack,
  Cancel,
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { resetPassword } from "../../services/authService";
import { useAuthStore } from "../../store/useAuthStore";
import { UserRole } from "../../types/user";
import { formatApiError } from "../../utils/formatApiError";
import { decodeJwtToken } from "../../utils/jwtUtils";
import {
  ResetPasswordFormData,
  getResetPasswordSchema,
} from "../../validation/authSchema";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const { setToken, setUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(getResetPasswordSchema()),
  });

  const newPassword = watch("newPassword");

  useEffect(() => {
    if (!token || !email) {
      showSnackbar(t("auth.invalidResetLinkSnackbar"), "error");
      navigate("/forgot-password");
    }
  }, [token, email, navigate, t]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token || !email) {
      showSnackbar(t("auth.invalidResetLinkSnackbar"), "error");
      navigate("/forgot-password");
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetPassword({
        email,
        token,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      });

      if (response.isSuccess) {
        setIsSuccess(true);
        showSnackbar(t("auth.passwordResetSuccessful"), "success");

        // If backend returns new token, update it and refresh user object
        // This allows user to continue using the system without logging out/in
        if (response.data?.token) {
          setToken(response.data.token);
          // Decode new token to update user object with updated hasSetPassword
          const decodedToken = decodeJwtToken(response.data.token);
          if (decodedToken) {
            // Update user object with new data from token
            setUser({
              id: decodedToken.id || "",
              email: decodedToken.email || "",
              firstName: decodedToken.firstName || "",
              lastName: decodedToken.lastName || "",
              role:
                (UserRole[
                  decodedToken.roles[0] as keyof typeof UserRole
                ] as UserRole) || UserRole.Customer,
              isVerified: decodedToken.isVerified || false,
              authType:
                (decodedToken.authType as
                  | "manual"
                  | "gmail"
                  | "phone"
                  | "google") || "manual",
              hasSetPassword: decodedToken.hasSetPassword ?? true,
              preferences: {
                language: "en",
                currency: "USD",
                notifications: { email: true, sms: false, push: true },
                theme: "light",
              },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
        }
      } else {
        showSnackbar(formatApiError(response), "error");
      }
    } catch (error) {
      showSnackbar(formatApiError(error), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleRequestNewLink = () => {
    navigate("/forgot-password");
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 480,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />

          <Typography variant="h4" component="h1" className="notranslate" translate="no" gutterBottom>
            {t("auth.passwordResetSuccessful")}
          </Typography>

          <Typography variant="body1" color="text.secondary" className="notranslate" translate="no" sx={{ mb: 3 }}>
            {t("auth.passwordResetSuccessDesc")}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            size="large"
            className="notranslate"
            translate="no"
            onClick={handleBackToLogin}
            startIcon={<ArrowBack />}
          >
            {t("auth.goToLogin")}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!token || !email) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 480,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Cancel sx={{ fontSize: 64, color: "error.main", mb: 2 }} />

          <Typography variant="h4" component="h1" className="notranslate" translate="no" gutterBottom>
            {t("auth.invalidResetLink")}
          </Typography>

          <Typography variant="body1" color="text.secondary" className="notranslate" translate="no" sx={{ mb: 3 }}>
            {t("auth.resetLinkExpired")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              className="notranslate"
              translate="no"
              onClick={handleRequestNewLink}
            >
              {t("auth.requestNewLink")}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              className="notranslate"
              translate="no"
              onClick={handleBackToLogin}
              startIcon={<ArrowBack />}
            >
              {t("auth.backToLogin")}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 520,
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" className="notranslate" translate="no" gutterBottom align="center">
          {t("auth.resetYourPassword")}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          className="notranslate"
          translate="no"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {t("auth.enterNewPassword")}
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register("newPassword")}
            label={t("auth.newPassword")}
            className="notranslate"
            translate="no"
            type={showPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message || t("validation.passwordMinSimple")}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            {...register("confirmNewPassword")}
            label={t("auth.confirmNewPassword")}
            className="notranslate"
            translate="no"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            error={!!errors.confirmNewPassword}
            helperText={errors.confirmNewPassword?.message}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              className="notranslate"
              translate="no"
              disabled={isLoading}
              startIcon={<Lock />}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("auth.resetPasswordButton")
              )}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              className="notranslate"
              translate="no"
              onClick={handleBackToLogin}
              disabled={isLoading}
              startIcon={<ArrowBack />}
            >
              {t("auth.backToLogin")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPassword;
